
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField, CircularProgress,
  Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, Box, AppBar,
  Toolbar, IconButton, CssBaseline, List, ListItem, ListItemText, InputAdornment, Menu, MenuItem,
  FormControl, InputLabel, Select, Dialog, DialogContent, DialogTitle, DialogActions
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, ELLIPSIS, ELLIPSIS_CSS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT } from '../../styles/recruiterTheme';
import ProfileForm from '../shared/Profileform.js';
import CandidateProfiles from '../shared/CandidateProfile.js';
import CandidateData from '../shared/CandidateData.js';
import Pagination from '../../utils/Pagination.js';
import JDDetails from "../shared/JDDetails.js";
import ResumeDisplay from "./ResumeDisplay.js";
import logo from '../../styles/logo.png';
import PendingData from '../shared/PendingData.js';
import NotificationDialog from '../shared/NotificationDialog';
import Reports from '../shared/Reports';
import QuestionDialog from '../shared/QuestionDialog.js';
import AllResumes from "./AllResumes.js";
import Skills from "./Skills.js"
import BenchPeople from "../HRPages/BenchPeople/Benchpeople.js";
// import StoredResumesPage from "./StoredResumesPage.js";
import {
  PendingActions as PendingActionsIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Widgets as WidgetsIcon,
  BarChart as BarChartIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon, PersonOutline,
  Business as BusinessIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Videocam as VideocamIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import useActivityLogger from '../../hooks/useActivityLogger';
import ClientSummaryReport from "../Reports/ClientSummaryReport.js";
// ─── Global Font ──────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  display: flex;
  flex-direction: row;
  background: #F3EEF9;
  min-height: 100vh;
  font-family: ${FONT_BODY};
  padding-top: 72px;
  overflow: hidden;
`;

const CenteredSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  height: calc(100vh - 72px);
  background: #F3EEF9;
`;

const ScrollableContent = styled.div`
  width: 100%;
  min-height: 100%;
  padding-bottom: 40px;
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  background: linear-gradient(170deg, #2D1B5E 0%, #4F378B 55%, #6750A4 100%);
  height: calc(100vh - 72px);
  padding: 24px 16px;
  position: sticky;
  top: 0;
  overflow-y: auto;
  box-shadow: 4px 0 24px rgba(79,55,139,0.18);
  z-index: 10;
`;

const SidebarLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY};
    font-size: ${TYPE.overline}px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1.25;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin: 16px 0 8px 12px;
  }
`;

const NavButton = styled(IconButton)`
  && {
    width: 100%;
    border-radius: ${RADIUS.sm}px;
    padding: 10px 12px;
    justify-content: flex-start;
    text-align: left;
    color: rgba(255,255,255,0.82);
    transition: background 0.18s ease, color 0.18s ease;
    margin-bottom: 4px;
    gap: 12px;
    font-family: ${FONT_BODY};
    font-weight: 600;
    font-size: ${TYPE.body}px;
    line-height: 1.25;
    position: relative;
    &:hover {
      background: rgba(255,255,255,0.14);
      color: #fff;
    }
    svg { font-size: 20px; flex-shrink: 0; }
  }
`;

const SidebarDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  margin: 12px 0;
`;

// ─── AppBar ───────────────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar)`
  && {
    background: rgba(255,255,255,0.97) !important;
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(103,80,164,0.12) !important;
    box-shadow: 0 2px 20px rgba(79,55,139,0.08) !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 1300 !important;
  }
`;

const BrandText = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 800 !important;
    font-size: ${TYPE.section}px !important;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
`;

const StyledAvatar = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-size: ${TYPE.body}px;
  font-weight: 700;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(79,55,139,0.35);
  flex-shrink: 0;
`;

const BellButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    transition: background 0.2s;
    position: relative;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

// ─── Page shell ("cards on canvas") ───────────────────────────────────────────
// Transparent container: the page sits on the lavender canvas, sections inside
// are their own white cards. Every tab renders inside it (embedded tab components
// add no outer padding of their own) so every title starts at the same x.
const StyledPaper = styled.div`
  padding: ${PAGE_PADDING}px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// White card around a table (or a section). overflow-x auto is only a fallback.
const TableCard = styled.div`
  background: #fff;
  border: 1px solid #EDE7F6;
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const SectionCard = styled.div`
  background: #fff;
  border: 1px solid #EDE7F6;
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  padding: 24px;
  min-width: 0;
`;

// Card toolbar (filters) sitting above the table inside the same card.
const CardToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 24px;
  border-bottom: 1px solid #EDE7F6;
`;

// Single-line cell text with a max width (pair with title= for the tooltip).
const CellEllipsis = styled.div`
  ${ELLIPSIS_CSS}
  max-width: ${p => p.maxWidth || 200}px;
`;

// Two-line clamp for long free text (role titles, recruiter lists).
const CellClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  word-break: break-word;
  ${p => p.minWidth ? `min-width: ${p.minWidth}px;` : ''}
  ${p => p.maxWidth ? `max-width: ${p.maxWidth}px;` : ''}
`;

// ─── Page Title ───────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin: 4px 0 24px !important;
  }
`;

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg, #6750A4, #4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(103,80,164,0.28)'};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px ${p => p.shadow || 'rgba(103,80,164,0.36)'};
  }
  &::after {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.22;
  svg { font-size: 40px; }
`;

// ─── User Card ────────────────────────────────────────────────────────────────
const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 4px;
  background: linear-gradient(135deg, #F8F4FF, #EDE7F6);
  border-radius: ${RADIUS.pill}px;
  border: 1px solid rgba(103,80,164,0.15);
  min-width: 160px;
  white-space: nowrap;
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTable = styled(Table)`
  && { border-collapse: separate; border-spacing: 0; }
  && tbody tr:last-child td { border-bottom: none !important; }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:hover td { background: #FAF7FF; }
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: #3D2B6B !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    white-space: nowrap;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover { transform: translateY(-1px); box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 32px;
    padding: 0 12px !important;
    white-space: nowrap;
    transition: background 0.18s !important;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

const LinkButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    min-width: 0 !important;
    padding: 4px 8px !important;
    margin-left: -8px;
    justify-content: flex-start;
    white-space: nowrap;
    line-height: 1.5;
    border-radius: ${RADIUS.sm}px !important;
    &:hover { background: rgba(103,80,164,0.06) !important; }
  }
`;

// ─── Notification ─────────────────────────────────────────────────────────────
const NotifItem = styled(MenuItem)`
  && {
    padding: 12px 16px !important;
    border-bottom: 1px solid #FAF7FF !important;
    transition: background 0.15s !important;
    &:hover { background: #F8F4FF !important; }
  }
`;

const NotifDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF6B6B, #FF4444);
  flex-shrink: 0;
  margin-top: 6px;
  box-shadow: 0 2px 6px rgba(255,68,68,0.4);
`;

// ─── Upload Zone ──────────────────────────────────────────────────────────────
const UploadZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 32px;
  border: 2px dashed #C4B0E8;
  border-radius: ${RADIUS.lg}px;
  background: linear-gradient(135deg, #FAF7FF, #F3EEF9);
  width: 100%;
  max-width: 560px;
  cursor: pointer;
  transition: all 0.22s ease;
  &:hover {
    border-color: #7C5CBF;
    background: linear-gradient(135deg, #F3EEF9, #EDE7F6);
  }
`;

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionAccent = styled.div`
  width: 4px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
`;

// ─── Date Filter ──────────────────────────────────────────────────────────────
const FilterField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
`;

const DateInput = styled.input`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: #3D2B6B;
  background: #F8F4FF;
  border: 1.5px solid #C4B0E8;
  border-radius: ${RADIUS.sm}px;
  height: 40px;
  padding: 0 12px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: #6750A4;
    box-shadow: 0 0 0 3px rgba(103,80,164,0.12);
  }
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    filter: invert(30%) sepia(60%) saturate(400%) hue-rotate(220deg);
  }
`;

const FilterLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  color: #8B7DB5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  height: 40px;
  line-height: 1;
`;

const ClearFilterBtn = styled(Button)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.caption}px !important;
    font-weight: 600 !important;
    color: #6750A4 !important;
    text-transform: none !important;
    border: 1.5px solid #C4B0E8 !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 40px;
    padding: 0 16px !important;
    background: #fff !important;
    &:hover { background: #EDE7F6 !important; }
  }
`;

const FilterResultPill = styled.div`
  background: #EDE7F6;
  color: #4a3380;
  border-radius: ${RADIUS.pill}px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-family: ${FONT_BODY};
  white-space: nowrap;
`;

// ─── Client Cards (Recruiter Dashboard "Clients" section) ─────────────────────
const ClientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-auto-rows: 1fr;
  gap: 16px;
  margin-bottom: 8px;
`;

const ClientCardBox = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  height: 100%;
  min-height: 112px;
  min-width: 0;
  background: #fff;
  border: 1.5px solid #EDE7F6;
  border-radius: ${RADIUS.md}px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #7C5CBF, #4F378B);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  &:hover {
    border-color: #7C5CBF;
    box-shadow: 0 12px 32px rgba(103,80,164,0.18);
    transform: translateY(-2px);
  }
  &:hover::before { opacity: 1; }
`;

const ClientAvatar = styled.div`
  width: 48px; height: 48px;
  border-radius: ${RADIUS.md}px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.section}px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(79,55,139,0.28);
`;

const ClientStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  background: ${p => p.bg || '#EDE7F6'};
  color: ${p => p.color || '#6750A4'};
  margin-top: 8px;
`;

const ClientJobCountBadge = styled.div`
  flex-shrink: 0;
  text-align: center;
  padding: 8px 16px;
  border-radius: ${RADIUS.md}px;
  background: #F3EEF9;
  min-width: 64px;
`;

const CLIENT_STATUS_META = {
  Active: { color: "#1B6B3A", bg: "#D4FAE0", dot: "#2E9B54" },
  Inactive: { color: "#5C4A8A", bg: "#F0EAF9", dot: "#9B8DC4" },
  "On Hold": { color: "#7A5200", bg: "#FFF3CD", dot: "#F59E0B" },
  Prospect: { color: "#1E3A8A", bg: "#DBEAFE", dot: "#3B82F6" },
};

// ─── Module-level constants ────────────────────────────────────────────────────
const API = {
  extractJobDetails: process.env.REACT_APP_EXTRACT_JOB_DETAILS_API,
  logout: process.env.REACT_APP_LOGOUT_API,
  getJobDescription: process.env.REACT_APP_JOB_DESCRIPTION_DATA_API,
  scheduledCandidates: process.env.REACT_APP_SCHEDULED_CANDIDATE_DETAILS_API,
  getPdf: process.env.REACT_APP_GET_PDF_API,
  allJobDescriptions: process.env.REACT_APP_ALL_JOB_DESCRIPTION_API,
  search: process.env.REACT_APP_SEARCH_API_URL,
  generatedQuestions: process.env.REACT_APP_GENERATED_QUESTIONS_URL,
  candidateProfileByJD: process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API,
  recruiterResumeUpload: process.env.REACT_APP_RECRUITER_RESUME_UPLOAD_API,
  resumeUploadAndSearch: process.env.REACT_APP_RESUME_UPLOAD_AND_SEARCH_API,
  getAllResumes: process.env.REACT_APP_GET_ALL_RESUMES_API,
  clients: process.env.REACT_APP_API_CLIENTS_URL,
  rescheduleInterview: process.env.REACT_APP_RESCHEDULE_INTERVIEW_API,
  searchStatus: process.env.REACT_APP_SEARCH_STATUS_API,
};

const PENDING_STATUSES = new Set([
  'HiringManager_Accepted',
  'ScreeningTest_Passed',
  'NewCandidate',
  'Round2_Suggested',
  'ReScreeningTest_Passed',
]);

const ITEMS_PER_PAGE = 10;


const PENDING_POLL_MS = 60_000;

const PENDING_DEBOUNCE_MS = 2_000;

const SUGGESTION_DEBOUNCE_MS = 300;

// Tabs that can be restored after a refresh. Transient views (upload, JD
// details, candidate profiles/data) are not persisted; on refresh the user
// lands back on the tab they were opened from.
const PERSISTABLE_TABS = new Set([
  'RecruiterDashboard', 'pendingData', 'Scheduled', 'AvailableJDs', 'resumes',
  'benchPeople', 'skillSet', 'report', 'summary', 'profile',
]);
const DEFAULT_TAB = 'RecruiterDashboard';
const TAB_KEY = 'activeTab';
const RETURN_TAB_KEY = 'returnTab';

const readSession = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeSession = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};

const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('rbotUser'));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toLocalTimeInput = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

// Display-only formatting: "29 Oct 2026" / "29 Oct 2026 · 11:00"; raw value if unparseable.
const formatDisplayDate = (raw) => {
  const d = new Date(raw);
  if (raw == null || raw === '' || Number.isNaN(d.getTime())) return raw ? String(raw) : '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatDisplayDateTime = (raw) => {
  const d = new Date(raw);
  if (raw == null || raw === '' || Number.isNaN(d.getTime())) return raw ? String(raw) : '—';
  return `${formatDisplayDate(raw)} · ${toLocalTimeInput(d)}`;
};

const LoadingBlock = ({ label }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} gap={1.5}>
    <CircularProgress sx={{ color: '#6750A4' }} />
    {label && (
      <Typography sx={{ fontFamily: FONT_BODY, color: '#8B7DB5', fontSize: TYPE.body, lineHeight: 1.5 }}>{label}</Typography>
    )}
  </Box>
);

const ErrorBlock = ({ message, onRetry }) => (
  <Box sx={{ textAlign: 'center', py: 5 }}>
    <Typography sx={{ color: '#E53935', fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, mb: onRetry ? 1.5 : 0 }}>
      {message}
    </Typography>
    {onRetry && <GhostButton onClick={onRetry}>Retry</GhostButton>}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
function RecruiterDashboard() {
  const [jds, setJds] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  // User fields come from navigation state (fresh login) or, after a refresh,
  // from the profile Login.js persisted in localStorage.
  const currentUser = useMemo(
    () => (location.state?.email ? location.state : readStoredUser()) || {},
    [location.state]
  );
  const { email, name, phone, userType, _id } = currentUser;
  const userData = useMemo(
    () => (email && userType ? { name: name || '', phone, email, userType, _id } : null),
    [name, phone, email, userType, _id]
  );
  const [activeTab, setActiveTab] = useState(() => {
    const saved = readSession(TAB_KEY);
    return saved && PERSISTABLE_TABS.has(saved) ? saved : DEFAULT_TAB;
  });
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const extractjobdetailsApiUrl = process.env.REACT_APP_EXTRACT_JOB_DETAILS_API;
  const logoutApiUrl = process.env.REACT_APP_LOGOUT_API;
  const getJobDescriptiondata = process.env.REACT_APP_JOB_DESCRIPTION_DATA_API;
  const scheduledCandidateDetailsApiUrl = process.env.REACT_APP_SCHEDULED_CANDIDATE_DETAILS_API;
  const getpdfApiUrl = process.env.REACT_APP_GET_PDF_API;
  const getAllJobDescriptiondata = process.env.REACT_APP_ALL_JOB_DESCRIPTION_API;
  const search = process.env.REACT_APP_SEARCH_API_URL;
  const [summaries, setSummaries] = useState([]);
  const [loadingSearchCandidatesMap, setLoadingSearchCandidatesMap] = useState({});
  const [showCandidateProfiles, setShowCandidateProfiles] = useState(false);
  const [selectedCandidateProfileId, setSelectedCandidateProfileId] = useState(null);
  const [selectedJDSkills, setSelectedJDSkills] = useState(null);
  const [selectedJDExperience, setSelectedJDExperience] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [resumeData, setResumeData] = useState(null);
  const [jobDescription, setJobDescription] = useState(null);
  const [salaryRange, setSalaryRange] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showPendingData, setShowPendingData] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [allJds, setAllJds] = useState([]);
  const [allJdsLoading, setAllJdsLoading] = useState(false);
  const [allJdsError, setAllJdsError] = useState('');
  const [jdsLoaded, setJdsLoaded] = useState(() => Boolean(location.state?.jds));
  const [jdsError, setJdsError] = useState('');
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledLoaded, setScheduledLoaded] = useState(false);
  const [scheduledError, setScheduledError] = useState('');
  const [searching, setSearching] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const [questionsJdId, setQuestionsJdId] = useState(null);
  const [questionsLoadingJdId, setQuestionsLoadingJdId] = useState(null);
  const [rescheduleError, setRescheduleError] = useState('');
  const [readOnly, setReadOnly] = useState([]);
  const [showCandidateData, setShowCandidateData] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [query, setQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElMap, setAnchorElMap] = useState({});
  const [uploadSource, setUploadSource] = useState(null);
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [subjectiveQuestions, setSubjectiveQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [currentPage, setCurrentPage] = useState({ jobDescriptions: 1, scheduledInterviews: 1, availableJDs: 1 });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const itemsPerPage = 10;
  const [matchedProfiles, setMatchedProfiles] = useState([]);
  const [jdResults, setJdResults] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [jdCandidatesMap, setJdCandidatesMap] = useState({});
  // const [recruiterId,               setRecruiterId]               = useState(null);
  const recruiterId = userData?._id || null;

  const [scheduleFromDate, setScheduleFromDate] = useState('');
  const [scheduleToDate, setScheduleToDate] = useState('');
  const { log } = useActivityLogger(email, userType);
  // ── Dashboard "Clients first" view state ────────────────────────────────────
  const [dashboardClients, setDashboardClients] = useState([]);
  // Starts true so the first paint shows a spinner instead of "No clients found."
  const [loadingDashboardClients, setLoadingDashboardClients] = useState(Boolean(API.clients));
  const [dashboardClientsError, setDashboardClientsError] = useState('');
  const [selectedDashboardClient, setSelectedDashboardClient] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleRow, setRescheduleRow] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleInterviewers, setRescheduleInterviewers] = useState([]);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const pollingRef = useRef(null);
  const pendingDebounceRef = useRef(null);
  // Interval/timeout of an in-flight upload-and-search status poll, cleared on unmount/logout.
  const searchPollRef = useRef({ intervalId: null, timeoutId: null });
  const uploadingRef = useRef(false);
  const fileInputRef = useRef(null);
  const searchSeqRef = useRef(0);
  const suggestionSeqRef = useRef(0);
  const suggestionTimerRef = useRef(null);

  const isDateFiltered = Boolean(scheduleFromDate || scheduleToDate);

  // Date filter runs over the full list; pagination is applied afterwards.
  const allFilteredScheduledInterviews = useMemo(() => {
    if (!scheduleFromDate && !scheduleToDate) return data;

    const from = scheduleFromDate ? new Date(`${scheduleFromDate}T00:00:00`) : null;
    const to = scheduleToDate ? new Date(`${scheduleToDate}T23:59:59.999`) : null;

    return data.filter(row => {
      const d = new Date(row.dateAndTime);
      if (Number.isNaN(d.getTime())) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [data, scheduleFromDate, scheduleToDate]);

  const filteredScheduledInterviews = useMemo(
    () => allFilteredScheduledInterviews.slice(
      (currentPage.scheduledInterviews - 1) * ITEMS_PER_PAGE,
      currentPage.scheduledInterviews * ITEMS_PER_PAGE
    ),
    [allFilteredScheduledInterviews, currentPage.scheduledInterviews]
  );

  const totalPages = {
    jobDescriptions: Math.max(1, Math.ceil(jds.length / itemsPerPage)),
    scheduledInterviews: Math.max(1, Math.ceil(allFilteredScheduledInterviews.length / itemsPerPage)),
    availableJDs: Math.ceil(allJds.length / itemsPerPage),
  };
  const handleNextPage = (dataset) => setCurrentPage(p => ({ ...p, [dataset]: Math.min(p[dataset] + 1, totalPages[dataset]) }));

  const notifications = useMemo(
    () =>
      jds.flatMap(jd => {
        const pending = jdCandidatesMap[jd._id] || [];
        return pending.length > 0
          ? [{
            jdID: jd.jdID,
            jobRole: jd.jobRole,
            count: pending.length,
            jd,
          }]
          : [];
      }),
    [jds, jdCandidatesMap]
  );

  // Any change to the date range starts again from the first page.
  useEffect(() => {
    setCurrentPage(p => (p.scheduledInterviews === 1 ? p : { ...p, scheduledInterviews: 1 }));
  }, [scheduleFromDate, scheduleToDate]);

  const sortedJds = useMemo(
    () => [...jds].sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time)),
    [jds]
  );

  // ── Client helpers (dashboard "Clients first" flow) ─────────────────────────
  // The client grid has its own filter box; it is independent of the candidate/JD search query.
  const filteredDashboardClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    if (!q) return dashboardClients;
    return dashboardClients.filter(c =>
      c.company_name?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q) ||
      c.primary_contact?.toLowerCase().includes(q)
    );
  }, [dashboardClients, clientFilter]);

  const clientJobCount = useCallback(
    (client) => sortedJds.filter(jd => jd.companyName === client.company_name).length,
    [sortedJds]
  );

  const dashboardClientJds = useMemo(() => {
    if (!selectedDashboardClient) return [];
    return sortedJds.filter(jd => jd.companyName === selectedDashboardClient.company_name);
  }, [sortedJds, selectedDashboardClient]);

  const fetchDashboardClients = useCallback(async () => {
    if (!API.clients) { setLoadingDashboardClients(false); return; }
    setLoadingDashboardClients(true);
    setDashboardClientsError('');
    try {
      const response = await axios.get(`${API.clients}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      setDashboardClients(response.data?.data || []);
    } catch (err) {
      console.error('[RecruiterDashboard] Failed to fetch clients:', err.message);
      setDashboardClientsError('Failed to load clients.');
      setDashboardClients([]);
    } finally {
      setLoadingDashboardClients(false);
    }
  }, []);

  //   useEffect(() => {
  //   if (dashboardClients.length === 0) return;
  //   const savedClientId = sessionStorage.getItem('selectedDashboardClientId');
  //   if (savedClientId) {
  //     const client = dashboardClients.find(c => c._id === savedClientId);
  //     if (client) setSelectedDashboardClient(client);
  //   }
  // }, [dashboardClients]);

  const handleSelectDashboardClient = useCallback((client) => {
    setSelectedDashboardClient(client);
    // sessionStorage.setItem('selectedDashboardClientId', client._id);
  }, []);

  const handleBackToClients = useCallback(() => {
    setSelectedDashboardClient(null);
    // sessionStorage.removeItem('selectedDashboardClientId');
  }, []);

  // Clears the search box without firing any search/suggestion request and
  // invalidates any search/suggestion response still in flight.
  const handleClearSearch = useCallback(() => {
    clearTimeout(suggestionTimerRef.current);
    searchSeqRef.current += 1;
    suggestionSeqRef.current += 1;
    setQuery('');
    setSuggestions([]);
    setMatchedProfiles([]);
    setJdResults([]);
    setError(null);
    setSearching(false);
    setHasSearched(false);
  }, []);

  // ── Pagination helpers ──────────────────────────────────────────────────────
  const getCurrentData = useCallback((dataset) => {
    const map = {
      jobDescriptions: jds,
      scheduledInterviews: allFilteredScheduledInterviews,
      availableJDs: allJds,
    };
    const src = map[dataset] || [];
    return src.slice(
      (currentPage[dataset] - 1) * ITEMS_PER_PAGE,
      currentPage[dataset] * ITEMS_PER_PAGE
    );
  }, [jds, allFilteredScheduledInterviews, allJds, currentPage]);

  const handlePrevPage = useCallback(
    (dataset) => setCurrentPage(p => ({ ...p, [dataset]: Math.max(p[dataset] - 1, 1) })),
    []
  );

  // const handleNextPage = useCallback(
  //   (dataset) => setCurrentPage(p => ({ ...p, [dataset]: Math.min(p[dataset] + 1, totalPages[dataset]) })),
  //   [totalPages]
  // );

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchJobDetails = useCallback(async () => {
    const em = userData?.email || localStorage.getItem('email');
    if (!em || !userData) return;
    try {
      const response = await axios.get(`${API.getJobDescription}/${em}`);
      if (response.status === 200) {
        if (userData.userType === 'recruiter') setJds(Array.isArray(response.data) ? response.data : []);
        else if (userData.userType === 'interviewer') setData(response.data);
        setJdsError('');
      }
    } catch (err) {
      // Polling errors are not logged loudly; the UI shows a retry only when nothing is loaded.
      console.debug('[RecruiterDashboard] fetchJobDetails error:', err.message);
      setJdsError('Failed to load your job descriptions.');
    } finally {
      setJdsLoaded(true);
    }
  }, [userData]);

  /**
   * Fetches pending candidate counts for each JD.
   *
   * - Called from the debounced jds-change effect below, so every poll tick
   *   (which replaces `jds`) refreshes the counts — candidates can change
   *   status even when the JD list itself is unchanged.
   * - Uses Promise.all so all JDs are fetched concurrently; per-JD failures
   *   are caught individually so one bad JD doesn't abort the rest.
   * - Map and count are built in one loop, then both setters are called
   *   together (batched by React 18) for a single re-render.
   */
  const fetchPendingCount = useCallback(async (currentJds) => {
    if (!currentJds?.length) {
      setJdCandidatesMap({});
      setPendingCount(0);
      return;
    }

    try {
      const responses = await Promise.all(
        currentJds.map(jd =>
          axios
            .get(`${API.candidateProfileByJD}/${jd._id}`)
            .then(res => ({ jdId: jd._id, data: res.data }))
            .catch(() => ({ jdId: jd._id, data: [] }))   // individual failure → empty
        )
      );

      const newMap = {};
      let totalPending = 0;
      for (const { jdId, data: candidates } of responses) {
        const pending = Array.isArray(candidates)
          ? candidates.filter(p => PENDING_STATUSES.has(p.overallStatus))
          : [];
        newMap[jdId] = pending;
        totalPending += pending.length;
      }

      setJdCandidatesMap(newMap);
      setPendingCount(totalPending);
    } catch (err) {
      // Non-critical background fetch — log at debug level only
      console.debug('[RecruiterDashboard] fetchPendingCount error:', err.message);
    }
  }, []); // no deps: reads API constant and PENDING_STATUSES which are module-level

  const fetchScheduledInterviews = useCallback(async (em) => {
    if (!em) return;
    setScheduledLoading(true);
    setScheduledError('');
    try {
      const response = await axios.get(API.scheduledCandidates, { params: { email: em } });
      if (response.status === 200 && response.data) {
        const rows = Array.isArray(response.data) ? response.data : [];
        setData(rows);
        localStorage.setItem("Scheduled", JSON.stringify(rows));
      }
    } catch (err) {
      console.error("[RecruiterDashboard] Failed to fetch scheduled interviews:", err.message);
      setScheduledError('Failed to load scheduled interviews.');
    } finally {
      setScheduledLoading(false);
      setScheduledLoaded(true);
    }
  }, []);

  const fetchAllJDs = useCallback(async () => {
    setAllJdsLoading(true);
    setAllJdsError('');
    try {
      const response = await axios.get(API.allJobDescriptions);
      if (response.status === 200) setAllJds(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("[RecruiterDashboard] Failed to fetch all JDs:", err.message);
      setAllJdsError('Failed to load active jobs.');
    } finally {
      setAllJdsLoading(false);
    }
  }, []);

  const stopSearchPolling = useCallback(() => {
    clearInterval(searchPollRef.current.intervalId);
    clearTimeout(searchPollRef.current.timeoutId);
    searchPollRef.current = { intervalId: null, timeoutId: null };
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Redirect to login only when there is no user at all (neither navigation
  // state nor the persisted profile). Missing `location.state.jds` is fine:
  // JDs are loaded by fetchJobDetails below.
  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  // Seed JDs from navigation state (fresh login) for an instant first paint.
  useEffect(() => {
    if (Array.isArray(location.state?.jds)) setJds(location.state.jds);
  }, [location.state]);

  // Bootstrap data + start polling when userData is ready
  useEffect(() => {
    if (!userData?.email) return;

    fetchScheduledInterviews(userData.email);
    fetchJobDetails();

    // Poll job details every PENDING_POLL_MS. Each tick replaces `jds`, which
    // triggers the debounced pending-count refresh below.
    pollingRef.current = setInterval(() => {
      fetchJobDetails();
    }, PENDING_POLL_MS);

    return () => clearInterval(pollingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  /**
   * Debounced pending-count refresh driven by JD list changes.
   *
   * On initial load `setJds` may be called more than once in quick succession
   * (navigation state, then the first fetch). The debounce window
   * (PENDING_DEBOUNCE_MS) swallows those rapid updates into one fetch.
   */
  useEffect(() => {
    if (!jdsLoaded) return;

    clearTimeout(pendingDebounceRef.current);
    pendingDebounceRef.current = setTimeout(() => {
      fetchPendingCount(jds);
    }, PENDING_DEBOUNCE_MS);

    return () => clearTimeout(pendingDebounceRef.current);
  }, [jds, jdsLoaded, fetchPendingCount]);

  // Persist the current tab so F5 restores it (transient views are skipped).
  useEffect(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(TAB_KEY, activeTab);
  }, [activeTab]);

  // Tab-specific data loads: on first mount for the restored tab, on every
  // switch, and when the same sidebar item is clicked again (tabRefreshKey).
  useEffect(() => {
    if (activeTab === 'AvailableJDs') fetchAllJDs();
    if (activeTab === 'RecruiterDashboard') fetchDashboardClients();
  }, [activeTab, tabRefreshKey, fetchAllJDs, fetchDashboardClients]);

  // Stop any in-flight upload/search status poll and suggestion timer on unmount.
  useEffect(() => () => {
    stopSearchPolling();
    clearTimeout(suggestionTimerRef.current);
  }, [stopSearchPolling]);

  // ── Action handlers ─────────────────────────────────────────────────────────
  const showSnackbar = useCallback((msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  // Closes every overlay/sub-view so exactly one view is visible.
  const closeSubViews = useCallback(() => {
    setIsViewJdDetailsOpen(false);
    setShowCandidateProfiles(false);
    setShowCandidateData(false);
    setShowPendingData(false);
  }, []);

  // Remember the tab a sub-view was opened from, so Back/Close returns there.
  const rememberReturnTab = useCallback(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(RETURN_TAB_KEY, activeTab);
  }, [activeTab]);

  /**
   * Single in-app tab switch used by the sidebar, Pending Actions, Profile and
   * PendingData's Back. Resets every sub-view and refetches that tab's data
   * (replaces the old window.location.reload() calls).
   */
  const switchTab = useCallback((tab) => {
    closeSubViews();
    setSelectedJD(null);
    removeSession(RETURN_TAB_KEY);
    if (tab === 'pendingData') setShowPendingData(true);
    if (tab === 'RecruiterDashboard') {
      setSelectedDashboardClient(null);
      fetchJobDetails();
      fetchScheduledInterviews(email);
    }
    if (tab === 'Scheduled') fetchScheduledInterviews(email);
    setActiveTab(tab);
    // Bumping the key refetches tab data (and remounts self-loading tab
    // components) even when the same sidebar item is clicked again.
    setTabRefreshKey(k => k + 1);
  }, [closeSubViews, fetchJobDetails, fetchScheduledInterviews, email]);

  const fetchQuestions = useCallback(async (jdID) => {
    if (!jdID) return;
    setQuestionsLoadingJdId(jdID);
    try {
      const response = await axios.get(`${API.generatedQuestions}/${jdID}`);
      setMcqQuestions(response.data?.mcq_questions || []);
      setCodingQuestions(response.data?.coding_questions || []);
      setSubjectiveQuestions(response.data?.subjective_questions || []);
      setQuestionsJdId(jdID);
      setOpen(true);
    } catch (err) {
      console.error("[RecruiterDashboard] Failed to fetch questions for JD:", jdID, err.message);
      showSnackbar(err.response?.data?.message || 'Failed to load questions for this JD. Please try again.');
    } finally {
      setQuestionsLoadingJdId(null);
    }
  }, [showSnackbar]);

  const handleViewCandidateProfiles = useCallback((
    jdId, skills, experience, jdDescription, jdSalaryRange, jdCompanyName, isReadOnly = false
  ) => {
    if (!jdId) return;
    rememberReturnTab();
    closeSubViews();
    setShowCandidateProfiles(true);
    setSelectedCandidateProfileId(jdId);
    setSelectedJDSkills(skills);
    setSelectedJDExperience(experience);
    setJobDescription(jdDescription);
    setSalaryRange(jdSalaryRange);
    setCompanyName(jdCompanyName);
    setSelectedUserType(userType);
    setActiveTab('');
    setReadOnly(isReadOnly);
  }, [rememberReturnTab, closeSubViews, userType]);

  const openTab = useCallback((tabName, jd = null) => {
    setActiveTab(tabName);
    if (tabName === "view-job-description") {
      setIsViewJdDetailsOpen(true);
      setSelectedJD({ ...jd, userRole: 'recruiter' });
    }
  }, []);

  const handleViewJD = useCallback((jd) => {
    if (!jd) return;
    rememberReturnTab();
    closeSubViews();
    const isReadOnly = activeTab === "AvailableJDs" || activeTab === "Scheduled";
    openTab("view-job-description", { ...jd, readOnly: isReadOnly });
  }, [activeTab, rememberReturnTab, closeSubViews, openTab]);

  const handleViewCandidateData = useCallback((candidate) => {
    if (!candidate) return;
    rememberReturnTab();
    closeSubViews();
    setActiveTab('');
    const normalized = {
      ...candidate,
      _id: candidate._id || candidate.candidateID,
    };

    setSelectedCandidate(normalized);
    setShowCandidateData(true);
  }, [rememberReturnTab, closeSubViews]);

  // Back/Close from JD details, candidate profiles or candidate data: return
  // to the tab the sub-view was opened from.
  const handleClose = useCallback(() => {
    const returnTab = readSession(RETURN_TAB_KEY);
    removeSession(RETURN_TAB_KEY);
    setSelectedJD(null);
    closeSubViews();
    setActiveTab(returnTab && PERSISTABLE_TABS.has(returnTab) ? returnTab : DEFAULT_TAB);
  }, [closeSubViews]);

  const handleProfileTabClick = useCallback(() => {
    switchTab('profile');
  }, [switchTab]);

  const handleLogout = useCallback(async () => {
    const em = email || localStorage.getItem('email');
    let tokens = {};
    try {
      tokens = JSON.parse(localStorage.getItem('userTokens')) || {};
    } catch {
      tokens = {};
    }
    const storedToken = localStorage.getItem('token');
    const token = (em && tokens[em]) || (storedToken && storedToken !== 'null' ? storedToken : null);

    stopSearchPolling();
    clearInterval(pollingRef.current);
    clearTimeout(pendingDebounceRef.current);
    clearTimeout(suggestionTimerRef.current);

    try {
      if (token) {
        await axios.post(API.logout, { token, email: em }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (err) {
      console.error("[RecruiterDashboard] Logout failed:", err.message);
    } finally {
      if (em) delete tokens[em];
      localStorage.setItem('userTokens', JSON.stringify(tokens));
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      localStorage.removeItem('rbotUser');
      removeSession(TAB_KEY);
      removeSession(RETURN_TAB_KEY);
      removeSession('prevTab');
      removeSession('selectedDashboardClientId');
      navigate('/login', { replace: true });
    }
  }, [email, navigate, stopSearchPolling]);

  const handleSearchCandidates = useCallback(async (jd) => {
    setLoadingSearchCandidatesMap(p => ({ ...p, [jd._id]: true }));
    try {
      const requestData = {
        job_description: jd.jobDescription,
        job_role: jd.jobRole,
        experience: jd.experience,
        jddetails_id: jd._id,
        jdID: jd.jdID,
        recruiterid: recruiterId || '',
      };
      await axios.post(API.extractJobDetails, requestData, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      setResumeData({ jdId: jd._id, summaries });
      setIsViewJdDetailsOpen(false);
      setSelectedJD({ ...jd, userRole: 'recruiter' });
      showSnackbar('Search completed successfully.');
      fetchJobDetails();
      log('SEARCH_BY_DOMAIN', {
        jdId: jd._id,
        targetName: jd.jobRole,
        metadata: { jdID: jd.jdID, skills: jd.skills, experience: jd.experience },
      });
    } catch (err) {
      console.error("[RecruiterDashboard] Candidate search failed:", err.message);
      if (err.response?.data?.error) {
        showSnackbar(err.response.data.error);
      } else if (err.request && !err.response) {
        // Connection dropped mid-search (server restart / network) — no reply at all.
        showSnackbar('The search was interrupted — no response from the server. Please try again.');
      } else {
        showSnackbar('Failed to fetch resumes from the model.');
      }
    } finally {
      setLoadingSearchCandidatesMap(p => ({ ...p, [jd._id]: false }));
    }
  }, [summaries, recruiterId, showSnackbar, fetchJobDetails, log]);

  const handlePendingCandidatesData = useCallback(() => {
    setSelectedUserType(userType);
    switchTab('pendingData');
  }, [userType, switchTab]);

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFileChange = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    // Reset so picking the same file again still fires onChange.
    event.target.value = '';
  }, []);

  const handleRemoveFile = useCallback((index, event) => {
    event.stopPropagation();
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    resetFileInput();
  }, [resetFileInput]);

  const pollSearchStatus = useCallback((jobId) => {
    return new Promise((resolve, reject) => {
      let networkFailures = 0;
      stopSearchPolling();
      const stop = stopSearchPolling;

      const intervalId = setInterval(async () => {
        try {
          const res = await fetch(`${API.searchStatus}/${jobId}`, { credentials: "include" });
          const data = await res.json();
          networkFailures = 0;
          if (data.status === "completed") {
            stop();
            resolve(data);
          } else if (data.status === "failed") {
            // Server-side failure, restart or timeout — the message explains which.
            stop();
            reject(new Error(data.error || "The search failed. Please try again."));
          }
          // else "processing" -- keep polling
        } catch (err) {
          // A single dropped poll is not a failed search; give up after a few in a row.
          networkFailures += 1;
          if (networkFailures >= 3) {
            stop();
            reject(new Error("Lost connection to the server while searching. Please check the results later or try again."));
          }
        }
      }, 3000); // poll every 3s

      // Stop watching after 5 minutes. The search keeps running on the server
      // and the recruiter gets an email when it finishes, so this is not a failure.
      const timeoutId = setTimeout(() => {
        stop();
        resolve({ stillRunning: true });
      }, 5 * 60 * 1000);

      searchPollRef.current = { intervalId, timeoutId };
    });
  }, [stopSearchPolling]);

  const handleResumeSubmit = useCallback(async (event, jd = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (uploadingRef.current) return;
    if (selectedFiles.length === 0) {
      showSnackbar('Please select files before submitting.');
      return;
    }
    if (!jd && !selectedDomain) {
      showSnackbar('Please select a domain before uploading.');
      return;
    }
    log('UPLOAD_RESUMES', { /* unchanged */ });

    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('files', file));

    let apiUrl = API.recruiterResumeUpload;
    let isSearchAPI = false;

    if (jd) {
      apiUrl = API.resumeUploadAndSearch;
      isSearchAPI = true;
      formData.append('jd._id', jd._id);
      formData.append('job_description', jd.jobDescription);
      formData.append('skills', jd.skills);
      formData.append('experience', jd.experience);
      formData.append('jdID', jd.jdID);
      formData.append('job_role', jd.jobRole);
      formData.append('recruiter_email', email);
      formData.append('recruiterid', recruiterId || '');
    } else {
      formData.append('domain', selectedDomain);
      formData.append('recruiter_email', email);
      formData.append('recruiterid', recruiterId || '');
    }

    uploadingRef.current = true;
    setUploading(true);
    try {
      const response = await fetch(apiUrl, { method: "POST", body: formData, credentials: "include" });
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      let d = {};
      if (isJson) {
        try { d = await response.json(); } catch { d = {}; }
      }

      if (!response.ok) {
        showSnackbar(d.message || d.error || `Upload failed (server returned ${response.status}). Please try again.`);
        return;
      }
      if (!isJson) {
        showSnackbar('The server returned an unexpected response. Please check the uploaded resumes and try again if needed.');
        return;
      }

      if (isSearchAPI && d.job_id) {
        showSnackbar("Files uploaded — searching for matches...");
        const result = await pollSearchStatus(d.job_id);
        if (result.stillRunning) {
          showSnackbar("The search is still running. You'll get an email when the results are ready.");
        } else {
          showSnackbar(result.search_success ? "Search completed successfully." : "No suitable resumes found.");
        }
      } else {
        showSnackbar(d.message || 'Files uploaded successfully.');
      }

      setSelectedFiles([]);
      resetFileInput();
      setSelectedDomain('');
      setActiveTab("RecruiterDashboard");
      if (isSearchAPI) {
        setSelectedJD(null);
        setUploadSource(null);
      }
      fetchJobDetails();
    } catch (err) {
      console.error("[RecruiterDashboard] Resume upload failed:", err.message);
      showSnackbar(err.message || "Failed to upload files. Please try again.");
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  }, [selectedFiles, selectedDomain, email, recruiterId, showSnackbar, log, pollSearchStatus, resetFileInput, fetchJobDetails]);

  const handleSearch = useCallback(async (searchValue) => {
    const q = String(searchValue ?? '').trim();
    if (!q) return;
    const em = email || localStorage.getItem('email');
    if (!em) { setError('No email found'); return; }

    // Invalidate pending suggestion requests and older searches.
    clearTimeout(suggestionTimerRef.current);
    suggestionSeqRef.current += 1;
    const seq = ++searchSeqRef.current;

    setHasSearched(true);
    setSearching(true);
    setMatchedProfiles([]);
    setJdResults([]);
    setError(null);
    try {
      const response = await axios.get(
        `${API.search}?query=${encodeURIComponent(q)}&userEmail=${encodeURIComponent(em)}`
      );
      if (seq !== searchSeqRef.current) return;
      const candidates = response.data?.candidates;
      const jdDetails = response.data?.jdDetails;

      if (Array.isArray(candidates) && candidates.length > 0) {
        setMatchedProfiles(candidates);
        setJdResults([]);
        setSuggestions(candidates.map(c => ({ type: 'candidate', name: c.candidatename, candidateID: c.candidateID })));
      } else if (Array.isArray(jdDetails) && jdDetails.length > 0) {
        setMatchedProfiles([]);
        setJdResults(jdDetails);
        setSuggestions(jdDetails.map(jd => ({ type: 'jd', jdID: jd.jdID, jobRole: jd.jobRole })));
      } else {
        setMatchedProfiles([]);
        setJdResults([]);
        setSuggestions([]);
      }
    } catch (err) {
      if (seq !== searchSeqRef.current) return;
      console.error("[RecruiterDashboard] Search failed:", err.message);
      setError("Something went wrong.");
      setMatchedProfiles([]);
      setJdResults([]);
      setSuggestions([]);
    } finally {
      if (seq === searchSeqRef.current) setSearching(false);
    }
  }, [email]);

  const handleSuggestionClick = useCallback((suggestion) => {
    let searchValue = "";
    if (suggestion.type === 'candidate') {
      searchValue = suggestion.candidateID || suggestion.candidateEmail || suggestion.phone || suggestion.name || "";
    } else if (suggestion.type === 'jd') {
      searchValue = suggestion.jdId || suggestion.jobRole || "";
    }
    setQuery(searchValue);
    handleSearch(searchValue);
  }, [handleSearch]);

  const fetchSuggestions = useCallback(async (value) => {
    if (!value) return;
    const em = email || localStorage.getItem("email");
    if (!em) return;
    const seq = ++suggestionSeqRef.current;
    try {
      const response = await axios.get(
        `${API.search}?query=${encodeURIComponent(value)}&userEmail=${encodeURIComponent(em)}`
      );
      // A newer keystroke (or a full search) superseded this request.
      if (seq !== suggestionSeqRef.current) return;
      const candidates = response.data?.candidates || [];
      const jdDetails = response.data?.jdDetails || [];

      const searchTerm = value.toLowerCase();
      const digitsOnly = value.replace(/\D/g, "");
      const isPhone = digitsOnly.length >= 3;
      const isEmail = value.includes("@");
      const isCandidateID = searchTerm.startsWith("cd-");
      const isJDID = searchTerm.startsWith("jd-");
      const isNumeric = /^\d+$/.test(value);

      let filtered = [];

      if (searchTerm === "cd") {
        filtered = candidates.map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.candidatename}`, candidateID: c.candidateID }));
      } else if (searchTerm === "jd") {
        filtered = jdDetails.map(jd => ({ type: 'jd', label: `${jd.jdID} - ${jd.jobRole}`, jdId: jd.jdID, jobRole: jd.jobRole }));
      } else if (isCandidateID) {
        const idPart = searchTerm.replace("cd-", "");
        filtered = candidates
          .filter(c => c.candidateID?.toLowerCase().includes(idPart))
          .map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.candidatename}`, candidateID: c.candidateID }));
      } else if (isJDID) {
        const idPart = searchTerm.replace("jd-", "");
        filtered = jdDetails
          .filter(jd => jd.jdID?.toLowerCase().includes(idPart))
          .map(jd => ({ type: 'jd', label: `${jd.jdID} - ${jd.jobRole}`, jdId: jd.jdID, jobRole: jd.jobRole }));
      } else if (isPhone || isNumeric) {
        filtered = [
          ...candidates.filter(c => c.phone?.replace(/\D/g, "").includes(digitsOnly)).map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.phone}`, candidateID: c.candidateID })),
          ...candidates.filter(c => c.candidateID?.toLowerCase().includes(searchTerm)).map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.candidatename}`, candidateID: c.candidateID })),
          ...jdDetails.filter(jd => jd.jdID?.toLowerCase().includes(searchTerm)).map(jd => ({ type: 'jd', label: `${jd.jdID} - ${jd.jobRole}`, jdId: jd.jdID, jobRole: jd.jobRole })),
        ];
      } else if (isEmail) {
        filtered = candidates
          .filter(c => c.candidateEmail?.toLowerCase().includes(searchTerm))
          .map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.candidateEmail}`, candidateID: c.candidateID }));
      } else {
        const nameMatches = candidates.filter(c => c.candidatename?.toLowerCase().includes(searchTerm)).map(c => ({ type: 'candidate', label: `${c.candidateID} - ${c.candidatename}`, candidateID: c.candidateID }));
        const jdMatches = jdDetails.filter(jd => jd.jobRole?.toLowerCase().includes(searchTerm)).map(jd => ({ type: 'jd', label: `${jd.jdID} - ${jd.jobRole}`, jdId: jd.jdID, jobRole: jd.jobRole }));
        filtered = [...nameMatches.slice(0, 5), ...jdMatches.slice(0, 5)];
      }

      // Deduplicate by stable key
      const unique = Array.from(new Map(filtered.map(item => [JSON.stringify(item), item])).values());
      setSuggestions(unique.slice(0, 10));
    } catch (err) {
      if (seq !== suggestionSeqRef.current) return;
      console.error("[RecruiterDashboard] Suggestion fetch failed:", err.message);
    }
  }, [email]);

  // Debounced wrapper: only the last keystroke within the window hits the API.
  const scheduleSuggestions = useCallback((value) => {
    clearTimeout(suggestionTimerRef.current);
    if (!value || !value.trim()) {
      suggestionSeqRef.current += 1;
      setSuggestions([]);
      return;
    }
    suggestionTimerRef.current = setTimeout(() => fetchSuggestions(value), SUGGESTION_DEBOUNCE_MS);
  }, [fetchSuggestions]);

  const handleOpenReschedule = useCallback((row) => {
    const d = new Date(row.dateAndTime);
    const valid = !Number.isNaN(d.getTime());
    setRescheduleRow(row);
    // Build the inputs from local date/time parts (toISOString would shift to UTC).
    setRescheduleDate(valid ? toLocalDateInput(d) : '');
    setRescheduleTime(valid ? toLocalTimeInput(d) : '');
    setRescheduleInterviewers(Array.isArray(row.interviewer) ? row.interviewer : []);
    setRescheduleReason('');
    setRescheduleError('');
    setRescheduleOpen(true);
  }, []);

  const handleRescheduleSubmit = useCallback(async () => {
    if (!rescheduleRow) return;
    if (!rescheduleDate || !rescheduleTime) {
      setRescheduleError('Please choose both a date and a time.');
      return;
    }
    const newTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
    if (Number.isNaN(newTime.getTime())) {
      setRescheduleError('The selected date or time is not valid.');
      return;
    }
    setRescheduleError('');
    setRescheduling(true);
    try {
      const payload = {
        interview_time: newTime.toISOString(),
        interviewerEmails: rescheduleInterviewers,
        changedBy: email,
        role: userType,
        reason: rescheduleReason,
      };
      const response = await axios.put(
        `${API.rescheduleInterview}/${rescheduleRow._id}`,
        payload
      );

      log('RESCHEDULE_INTERVIEW', {
        jdId: rescheduleRow.jddetails_id?._id,
        targetName: rescheduleRow.candidate?.candidatename,
        metadata: {
          oldTime: rescheduleRow.dateAndTime,
          newTime: payload.interview_time,
          interviewers: rescheduleInterviewers,
          reason: rescheduleReason,
        },
      });
      showSnackbar(response.data?.message || 'Interview rescheduled successfully.');
      setRescheduleOpen(false);
      fetchScheduledInterviews(email);
    } catch (err) {
      console.error('[RecruiterDashboard] Reschedule failed:', err.message);
      setRescheduleError(err.response?.data?.message || 'Failed to reschedule interview. Please try again.');
    } finally {
      setRescheduling(false);
    }
  }, [rescheduleRow, rescheduleDate, rescheduleTime, rescheduleInterviewers, rescheduleReason, email, userType, log, showSnackbar, fetchScheduledInterviews]);

  const recruiterEmail = userData?.email || email;
  const clientsInitialLoading = loadingDashboardClients && dashboardClients.length === 0;
  const dashboardInitialLoading = !jdsLoaded || clientsInitialLoading;

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
      <GlobalStyle />
      <Container>
        <CssBaseline />

        {/* ── AppBar ── */}
        <StyledAppBar position="fixed">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 72, padding: '0 24px' }}>

            {/* Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '24px', borderRight: '1.5px solid rgba(103,80,164,0.1)' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: `${RADIUS.md}px`, background: 'linear-gradient(135deg, #EDE7F6, #D0BCFF)', border: '2px solid rgba(103,80,164,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(79,55,139,0.18)', flexShrink: 0 }}>
                <img src={logo} alt="Logo" style={{ height: 36, width: 36, borderRadius: RADIUS.sm }} />
              </Box>
              <Box>
                <BrandText>Resourcing Bot</BrandText>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#8B7DB5', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.25, mt: '4px' }}>
                  Recruitment Platform
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Bell */}
              <BellButton onClick={(e) => setNotifAnchor(e.currentTarget)}>
                <NotificationsIcon />
                {notifications.length > 0 && (
                  <Box sx={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: '8px', background: 'linear-gradient(135deg, #FF6B6B, #FF4444)', color: '#fff', fontSize: TYPE.overline - 1, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: '3px', boxShadow: '0 2px 6px rgba(255,68,68,0.5)', border: '1.5px solid #fff', lineHeight: 1, pointerEvents: 'none' }}>
                    {notifications.length > 99 ? '99+' : notifications.length}
                  </Box>
                )}
              </BellButton>

              <Menu anchorEl={notifAnchor} open={Boolean(notifAnchor)} onClose={() => setNotifAnchor(null)}
                PaperProps={{ sx: { width: 380, maxHeight: 420, borderRadius: `${RADIUS.lg}px`, boxShadow: '0 12px 40px rgba(79,55,139,0.18)', border: '1px solid #EDE7F6', mt: 1 } }}>
                {notifications.length === 0 ? (
                  <Box sx={{ p: '24px', textAlign: 'center', color: '#8B7DB5', fontFamily: FONT_BODY, fontSize: TYPE.body }}>No new notifications</Box>
                ) : (
                  notifications.map((n, i) => (
                    <NotifItem key={i} onClick={() => {
                      handleViewCandidateProfiles(n.jd._id, n.jd.skills, n.jd.experience, n.jd.jobDescription, n.jd.salaryRange, n.jd.companyName, false);
                      setNotifAnchor(null);
                    }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
                        <NotifDot />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: '#2D1B5E', lineHeight: 1.5, ...ELLIPSIS }} title={`${n.jdID} — ${n.jobRole}`}>{n.jdID} — {n.jobRole}</Typography>
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: '#8B7DB5', lineHeight: 1.5 }}>{n.count} candidate(s) awaiting review</Typography>
                        </Box>
                      </Box>
                    </NotifItem>
                  ))
                )}
              </Menu>

              {userData && (
                <UserCard>
                  <StyledAvatar>{userData.name.charAt(0)}</StyledAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: '#2D1B5E', lineHeight: 1.25 }}>{userData.name}</Typography>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: TYPE.caption, color: '#8B7DB5', lineHeight: 1.25, textTransform: 'capitalize' }}>{userData.userType}</Typography>
                  </Box>
                </UserCard>
              )}
            </Box>
          </Toolbar>
        </StyledAppBar>

        {/* ── Sidebar ── */}
        <MenuContainer>
          <SidebarLabel>Workspace</SidebarLabel>
          <NavButton onClick={() => { handleClearSearch(); switchTab("RecruiterDashboard"); }}>
            <WidgetsIcon /><span>Dashboard</span>
          </NavButton>
          <NavButton onClick={handlePendingCandidatesData}>
            <PendingActionsIcon /><span>Pending Actions</span>
            {pendingCount > 0 && (
              <Box sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 12, minWidth: 20, height: 20, borderRadius: '10px', background: 'linear-gradient(135deg, #FF6B6B, #FF4444)', color: '#fff', fontSize: TYPE.overline, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: '4px', boxShadow: '0 2px 6px rgba(255,68,68,0.5)', lineHeight: 1 }}>
                {pendingCount > 99 ? '99+' : pendingCount}
              </Box>
            )}
          </NavButton>
          <NavButton onClick={() => switchTab("Scheduled")}>
            <AccessTimeIcon /><span>Scheduled Interviews</span>
          </NavButton>
          <NavButton onClick={() => switchTab("AvailableJDs")}>
            <CheckCircleIcon /><span>Active Jobs</span>
          </NavButton>
          <NavButton onClick={() => switchTab("resumes")}>
            <DescriptionIcon /><span>Resumes</span>
          </NavButton>

          <NavButton onClick={() => switchTab("benchPeople")}>
            <PersonOutline /><span>Bench People</span>
          </NavButton>

          <NavButton onClick={() => switchTab("skillSet")}>
            <PsychologyIcon />
            <span>Skill Set</span>
          </NavButton>
          <SidebarDivider />
          <SidebarLabel>Analytics</SidebarLabel>
          <NavButton onClick={() => switchTab('report')}>
            <BarChartIcon /><span>Reports</span>
          </NavButton>
          <NavButton onClick={() => switchTab('summary')}>
            <AssessmentIcon /><span>Summary</span>
          </NavButton>
          <NavButton onClick={() => navigate('/proctoring')}>
            <VideocamIcon /><span>Camera Proctoring</span>
          </NavButton>
          <SidebarDivider />
          <SidebarLabel>Account</SidebarLabel>
          <NavButton onClick={handleProfileTabClick}><PersonIcon /><span>Profile</span></NavButton>
          <NavButton onClick={handleLogout}><ExitToAppIcon /><span>Logout</span></NavButton>
        </MenuContainer>

        {/* ── Main Content ── */}
        <CenteredSection>
          <ScrollableContent>

            {activeTab === 'profile' && <ProfileForm key={tabRefreshKey} userData={userData} />}

            {/* ── Recruitment Dashboard ── */}
            {activeTab === 'RecruiterDashboard' && (
              <StyledPaper>
                <Box sx={{ mb: 3 }}>
                  <PageTitle>Recruitment Dashboard</PageTitle>
                  <PageSubtitle sx={{ mb: '0 !important' }}>Track, search and manage your job descriptions and candidates</PageSubtitle>
                </Box>
                <StatGrid>
                  <StatCard gradient="linear-gradient(135deg, #6750A4, #4F378B)" shadow="rgba(103,80,164,0.32)">
                    <StatText><StatNumber>{jdsLoaded ? jds.length : '—'}</StatNumber><StatLabel>Active JDs</StatLabel></StatText><StatIcon><WidgetsIcon /></StatIcon>
                  </StatCard>
                  <StatCard gradient="linear-gradient(135deg, #E91E8C, #C2185B)" shadow="rgba(233,30,140,0.28)">
                    <StatText><StatNumber>{jdsLoaded ? pendingCount : '—'}</StatNumber><StatLabel>Pending</StatLabel></StatText><StatIcon><PendingActionsIcon /></StatIcon>
                  </StatCard>
                  <StatCard gradient="linear-gradient(135deg, #00897B, #00695C)" shadow="rgba(0,137,123,0.28)">
                    <StatText><StatNumber>{scheduledLoaded ? data.length : '—'}</StatNumber><StatLabel>Scheduled</StatLabel></StatText><StatIcon><AccessTimeIcon /></StatIcon>
                  </StatCard>
                </StatGrid>

                {/* Search */}
                <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ mb: 3, gap: 1.5 }}>
                  <Autocomplete
                    freeSolo
                    options={suggestions}
                    inputValue={query}
                    onInputChange={(event, newInputValue) => {
                      setQuery(newInputValue);
                      setError(null);
                      scheduleSuggestions(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') { setQuery(newValue); handleSearch(newValue); }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} onMouseDown={() => handleSuggestionClick(option)}>
                        {option.label || (option.type === 'candidate' ? option.name : option.jobRole) || "No Label"}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search by name, ID, role, client…"
                        variant="outlined"
                        size="small"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(query); } }}
                        sx={{ width: 380, maxWidth: '100%', '& .MuiOutlinedInput-root': { minHeight: CONTROL_HEIGHT.lg, background: '#fff', borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.body, '&:hover fieldset': { borderColor: '#7C5CBF' }, '&.Mui-focused fieldset': { borderColor: '#6750A4' } }, '& .MuiInputLabel-root': { fontFamily: FONT_BODY, fontSize: TYPE.body } }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#8B7DB5', fontSize: 20 }} /></InputAdornment>,
                          endAdornment: query ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleClearSearch} sx={{ color: '#B0A0D0' }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : params.InputProps.endAdornment,
                        }}
                      />
                    )}
                  />
                  <PrimaryButton
                    style={{ height: CONTROL_HEIGHT.lg }}
                    onClick={() => handleSearch(query)}
                    disabled={searching || !query.trim()}
                    startIcon={searching ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                  >
                    {searching ? 'Searching…' : 'Search'}
                  </PrimaryButton>
                </Box>

                {error && <Typography sx={{ color: '#E53935', fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, mb: 2 }}>{error}</Typography>}

                {searching && <LoadingBlock label="Searching…" />}

                {hasSearched && matchedProfiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <SectionHeader>
                      <SectionAccent />
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E' }}>Matched Profiles</Typography>
                    </SectionHeader>
                    <TableCard><TableScroll>
                    <StyledTable>
                      <TableHead><TableRow>
                        <HeadCell>Candidate ID</HeadCell><HeadCell>Name</HeadCell><HeadCell>Email</HeadCell><HeadCell>Phone</HeadCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {matchedProfiles.map(profile => (
                          <BodyRow key={profile.candidateID}>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}><LinkButton onClick={() => handleViewCandidateData(profile)}>{profile.candidateID}</LinkButton></BodyCell>
                            <BodyCell sx={{ fontWeight: '600 !important' }}><CellEllipsis maxWidth={240} title={profile.candidatename || undefined}>{profile.candidatename}</CellEllipsis></BodyCell>
                            <BodyCell><CellEllipsis maxWidth={280} title={profile.candidateEmail || undefined}>{profile.candidateEmail}</CellEllipsis></BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>{profile.phone}</BodyCell>
                          </BodyRow>
                        ))}
                      </TableBody>
                    </StyledTable>
                    </TableScroll></TableCard>
                  </Box>
                )}

                {hasSearched && matchedProfiles.length === 0 && jdResults.length > 0 && (
                  <TableCard style={{ marginTop: 16 }}><TableScroll>
                  <StyledTable>
                    <TableHead><TableRow>
                      <HeadCell>JD ID</HeadCell><HeadCell>Job Role</HeadCell><HeadCell>Created</HeadCell>
                      <HeadCell>Search</HeadCell><HeadCell>Candidates</HeadCell><HeadCell>Questions</HeadCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {jdResults.map(jd => (
                        <BodyRow key={jd._id}>
                          <BodyCell sx={{ whiteSpace: 'nowrap' }}><LinkButton onClick={() => handleViewJD(jd)}>{jd.jdID}</LinkButton></BodyCell>
                          <BodyCell sx={{ fontWeight: '600 !important' }}><CellClamp minWidth={200} title={jd.jobRole || undefined}>{jd.jobRole}</CellClamp></BodyCell>
                          <BodyCell sx={{ whiteSpace: 'nowrap' }}>{formatDisplayDate(jd.creation_time)}</BodyCell>
                          <BodyCell>
                            {loadingSearchCandidatesMap[jd._id] ? <CircularProgress size={20} /> : (
                              <>
                                <GhostButton onClick={(e) => setAnchorElMap(p => ({ ...p, [jd._id]: e.currentTarget }))} disabled={!jd.processed}>Search</GhostButton>
                                <Menu anchorEl={anchorElMap[jd._id]} open={Boolean(anchorElMap[jd._id])} onClose={() => setAnchorElMap(p => ({ ...p, [jd._id]: null }))}>
                                  <MenuItem onClick={() => { setUploadSource("dashboard"); setActiveTab("upload"); setAnchorElMap(p => ({ ...p, [jd._id]: null })); setSelectedJD(jd); }} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small }}>Upload My Own Resumes</MenuItem>
                                </Menu>
                              </>
                            )}
                          </BodyCell>
                          <BodyCell><GhostButton onClick={() => handleViewCandidateProfiles(jd._id, jd.skills, jd.experience, jd.jobDescription, jd.salaryRange, jd.companyName)}>View</GhostButton></BodyCell>
                          <BodyCell><GhostButton onClick={() => fetchQuestions(jd.jdID)} disabled={questionsLoadingJdId === jd.jdID}>{questionsLoadingJdId === jd.jdID ? <CircularProgress size={16} sx={{ color: '#6750A4' }} /> : 'View'}</GhostButton></BodyCell>
                        </BodyRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                  </TableScroll></TableCard>
                )}

                {/* ── Clients first, then that client's JDs ── */}
                {!hasSearched && jdsError && jds.length === 0 && jdsLoaded && (
                  <ErrorBlock message={jdsError} onRetry={fetchJobDetails} />
                )}

                {!hasSearched && (
                  <Box>
                    {!selectedDashboardClient ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 4, mb: 2 }}>
                          <SectionHeader style={{ marginBottom: 0 }}>
                            <SectionAccent />
                            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E' }}>Clients</Typography>
                          </SectionHeader>
                          {dashboardClients.length > 0 && (
                            <TextField
                              size="small"
                              placeholder="Filter clients…"
                              value={clientFilter}
                              onChange={(e) => setClientFilter(e.target.value)}
                              sx={{ width: 260, maxWidth: '100%', '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT.lg, background: '#fff', borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.small } }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#8B7DB5', fontSize: 18 }} /></InputAdornment>,
                                endAdornment: clientFilter ? (
                                  <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setClientFilter('')} sx={{ color: '#B0A0D0' }}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          )}
                        </Box>

                        {dashboardInitialLoading ? (
                          <LoadingBlock label="Loading your clients and job descriptions…" />
                        ) : dashboardClientsError && dashboardClients.length === 0 ? (
                          <ErrorBlock message={dashboardClientsError} onRetry={fetchDashboardClients} />
                        ) : filteredDashboardClients.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontFamily: FONT_BODY, color: '#B0A0D0', fontSize: TYPE.body, lineHeight: 1.5 }}>
                              {dashboardClients.length === 0 ? 'No clients found.' : 'No clients match your filter.'}
                            </Typography>
                          </Box>
                        ) : (
                          <ClientGrid>
                            {filteredDashboardClients.map(client => {
                              const sm = CLIENT_STATUS_META[client.relationship_status] || CLIENT_STATUS_META['Inactive'];
                              const jobCnt = clientJobCount(client);
                              return (
                                <ClientCardBox key={client._id} onClick={() => handleSelectDashboardClient(client)}>
                                  <ClientAvatar>{client.company_name?.[0]?.toUpperCase() || '?'}</ClientAvatar>
                                  <Box flex={1} minWidth={0}>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, lineHeight: 1.25, color: '#2D1B5E', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }} title={client.company_name}>
                                      {client.company_name}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, lineHeight: 1.5, color: '#8B7DB5', mt: 0.5, ...ELLIPSIS }} title={client.industry || undefined}>
                                      {client.industry || '—'}
                                    </Typography>
                                    <ClientStatusPill bg={sm.bg} color={sm.color}>
                                      <FiberManualRecordIcon sx={{ fontSize: 8, color: sm.dot }} />
                                      {client.relationship_status || 'Inactive'}
                                    </ClientStatusPill>
                                  </Box>
                                  <ClientJobCountBadge>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 800, fontSize: TYPE.section, color: '#4F378B', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                      {jobCnt}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#8B7DB5', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.5 }}>
                                      JD{jobCnt !== 1 ? 's' : ''}
                                    </Typography>
                                  </ClientJobCountBadge>
                                </ClientCardBox>
                              );
                            })}
                          </ClientGrid>
                        )}
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                          <IconButton onClick={handleBackToClients}
                            sx={{ width: 36, height: 36, background: '#F3EEF9', borderRadius: `${RADIUS.sm}px`, '&:hover': { background: '#EDE7F6' } }}>
                            <ArrowBackIcon sx={{ color: '#4F378B' }} />
                          </IconButton>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <ClientAvatar style={{ width: 40, height: 40, fontSize: TYPE.cardTitle }}>
                              {selectedDashboardClient.company_name?.[0]?.toUpperCase() || '?'}
                            </ClientAvatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E', ...ELLIPSIS }} title={selectedDashboardClient.company_name}>
                                {selectedDashboardClient.company_name}
                              </Typography>
                              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5, color: '#8B7DB5' }}>
                                {dashboardClientJds.length} job description{dashboardClientJds.length !== 1 ? 's' : ''} for this client
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {!jdsLoaded ? (
                          <LoadingBlock label="Loading job descriptions…" />
                        ) : dashboardClientJds.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontFamily: FONT_BODY, color: '#B0A0D0', fontSize: TYPE.body, lineHeight: 1.5 }}>
                              No job descriptions found for this client yet.
                            </Typography>
                          </Box>
                        ) : (
                          <TableCard><TableScroll>
                          <StyledTable>
                            <TableHead><TableRow>
                              <HeadCell>JD ID</HeadCell><HeadCell>Job Role</HeadCell><HeadCell>Created</HeadCell>
                              <HeadCell>Search</HeadCell><HeadCell>Candidates</HeadCell><HeadCell>Questions</HeadCell>
                            </TableRow></TableHead>
                            <TableBody>
                              {dashboardClientJds.map(jd => (
                                <BodyRow key={jd._id}>
                                  <BodyCell sx={{ whiteSpace: 'nowrap' }}><LinkButton onClick={() => handleViewJD(jd)}>{jd.jdID}</LinkButton></BodyCell>
                                  <BodyCell sx={{ fontWeight: '600 !important' }}><CellClamp minWidth={200} title={jd.jobRole || undefined}>{jd.jobRole}</CellClamp></BodyCell>
                                  <BodyCell sx={{ whiteSpace: 'nowrap' }}>{formatDisplayDate(jd.creation_time)}</BodyCell>
                                  <BodyCell>
                                    {loadingSearchCandidatesMap[jd._id] ? <CircularProgress size={20} /> : (
                                      <>
                                        <GhostButton onClick={(e) => setAnchorElMap(p => ({ ...p, [jd._id]: e.currentTarget }))} disabled={!jd.processed}>Search</GhostButton>
                                        <Menu anchorEl={anchorElMap[jd._id]} open={Boolean(anchorElMap[jd._id])} onClose={() => setAnchorElMap(p => ({ ...p, [jd._id]: null }))}>
                                          <MenuItem onClick={() => { handleSearchCandidates(jd); setAnchorElMap(p => ({ ...p, [jd._id]: null })); }} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small }}>Search By Domain</MenuItem>
                                          <MenuItem onClick={() => { setUploadSource("dashboard"); setActiveTab("upload"); setAnchorElMap(p => ({ ...p, [jd._id]: null })); setSelectedJD(jd); }} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small }}>Upload My Own Resumes</MenuItem>                                       </Menu>
                                      </>
                                    )}
                                  </BodyCell>
                                  <BodyCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      sx={{ color: "#6750A4" }}
                                      onClick={() =>
                                        handleViewCandidateProfiles(
                                          jd._id,
                                          jd.skills,
                                          jd.experience,
                                          jd.jobDescription,
                                          jd.salaryRange,
                                          jd.companyName
                                        )
                                      }
                                    >
                                      <VisibilityIcon />
                                    </IconButton>

                                    <IconButton
                                      size="small"
                                      sx={{ color: "#6750A4" }}
                                      onClick={() => {
                                        setUploadSource("dashboard");
                                        setActiveTab("upload");
                                        setAnchorElMap((p) => ({ ...p, [jd._id]: null }));
                                        setSelectedJD(jd);
                                      }}
                                    >
                                      <AddIcon />
                                    </IconButton>
                                    </Box>
                                  </BodyCell>
                                  <BodyCell><GhostButton onClick={() => fetchQuestions(jd.jdID)} disabled={questionsLoadingJdId === jd.jdID}>{questionsLoadingJdId === jd.jdID ? <><CircularProgress size={14} sx={{ color: '#6750A4', mr: 1 }} />Loading…</> : 'View Questions'}</GhostButton></BodyCell>
                                </BodyRow>
                              ))}
                            </TableBody>
                          </StyledTable>
                          </TableScroll></TableCard>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {hasSearched && !searching && !error && matchedProfiles.length === 0 && jdResults.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography sx={{ fontFamily: FONT_BODY, color: '#B0A0D0', fontSize: TYPE.body, lineHeight: 1.5 }}>No records found for your search.</Typography>
                  </Box>
                )}
              </StyledPaper>
            )}

            {/* ── Active Jobs ── */}
            {activeTab === 'AvailableJDs' && (
              <StyledPaper>
                <PageTitle>Active Jobs</PageTitle>
                <PageSubtitle>Browse all available job descriptions across the platform</PageSubtitle>
                {allJdsLoading && allJds.length === 0 ? (
                  <LoadingBlock label="Loading active jobs…" />
                ) : allJdsError && allJds.length === 0 ? (
                  <ErrorBlock message={allJdsError} onRetry={fetchAllJDs} />
                ) : allJds.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography sx={{ fontFamily: FONT_BODY, color: '#B0A0D0', fontSize: TYPE.body, lineHeight: 1.5 }}>No available JDs</Typography>
                  </Box>
                ) : (
                  <TableCard><TableScroll>
                  <StyledTable>
                    <TableHead><TableRow>
                      <HeadCell>JD ID</HeadCell><HeadCell>Job Role</HeadCell><HeadCell>Company</HeadCell>
                      <HeadCell>Recruiters</HeadCell><HeadCell sx={{ textAlign: 'right' }}>Candidates</HeadCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {getCurrentData('availableJDs').map(jd => {
                        const recruiterList = (Array.isArray(jd.recruiterContacts) ? jd.recruiterContacts : []).map(rec => rec?.email).filter(Boolean);
                        const recruiters = recruiterList.join(', ');
                        return (
                        <BodyRow key={jd._id}>
                          <BodyCell sx={{ whiteSpace: 'nowrap' }}><LinkButton onClick={() => handleViewJD(jd)}>{jd.jdID}</LinkButton></BodyCell>
                          <BodyCell sx={{ fontWeight: '600 !important' }}><CellClamp minWidth={220} title={jd.jobRole || undefined}>{jd.jobRole}</CellClamp></BodyCell>
                          <BodyCell><CellClamp maxWidth={220} title={jd.companyName || undefined}>{jd.companyName}</CellClamp></BodyCell>
                          <BodyCell sx={{ fontSize: `${TYPE.caption}px !important`, color: '#8B7DB5 !important' }}><Box title={recruiters || undefined} sx={{ maxWidth: 260 }}>
                            {recruiterList.length === 0 ? '—' : (<>
                              <Box sx={{ ...ELLIPSIS, display: 'block' }}>{recruiterList[0]}</Box>
                              {recruiterList.length > 1 && <Box component="span" sx={{ fontSize: TYPE.caption, fontWeight: 600, color: '#6750A4' }}>+{recruiterList.length - 1} more</Box>}
                            </>)}
                          </Box></BodyCell>
                          <BodyCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}><GhostButton onClick={() => handleViewCandidateProfiles(jd._id, jd.skills, jd.experience, jd.jobDescription, jd.salaryRange, jd.companyName, true)}>View Candidates</GhostButton></BodyCell>
                        </BodyRow>
                        );
                      })}
                    </TableBody>
                  </StyledTable>
                  </TableScroll></TableCard>
                )}
                {getCurrentData('availableJDs').length > 0 && totalPages.availableJDs > 1 && (
                  <Pagination itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage.availableJDs} totalPages={totalPages.availableJDs} handlePrevPage={() => handlePrevPage('availableJDs')} handleNextPage={() => handleNextPage('availableJDs')} />
                )}
              </StyledPaper>
            )}

            {/* ── skillSet ── */}
            {activeTab === "skillSet" && (
              <StyledPaper><Skills key={tabRefreshKey} /></StyledPaper>
            )}

            {/* ── BenchPeople ── */}
            {activeTab === "benchPeople" && (
              <StyledPaper><BenchPeople key={tabRefreshKey} /></StyledPaper>
            )}

            {/* ── Resumes ── */}
            {activeTab === 'resumes' && (
              <StyledPaper>
                <AllResumes
                  key={tabRefreshKey}
                  email={email}
                  selectedJD={selectedJD}
                  onSetSnackbar={(msg) => { setSnackbarMessage(msg); setSnackbarOpen(true); }}
                />
              </StyledPaper>
            )}

            {/* ── Stored Resumes ── */}
            {/* {activeTab === "storedresumes" && (
                <StoredResumesPage />
            )} */}





            {/* ── Reports ── */}

            {activeTab === 'report' && (
              <StyledPaper>
                <PageTitle>Reports</PageTitle>
                <PageSubtitle>Analytics and insights for your recruitment pipeline</PageSubtitle>
                <Reports key={tabRefreshKey} />
              </StyledPaper>
            )}
            {activeTab === 'summary' && (
              <StyledPaper><ClientSummaryReport key={tabRefreshKey} /></StyledPaper>
            )}
            {/* ── Scheduled Interviews ── */}
            {activeTab === 'Scheduled' && (
              <StyledPaper>
                <PageTitle>Scheduled Interviews</PageTitle>
                <PageSubtitle>View and manage all upcoming interview sessions</PageSubtitle>
                {scheduledError && (
                  <ErrorBlock message={scheduledError} onRetry={() => fetchScheduledInterviews(email)} />
                )}
                <TableCard>
                <CardToolbar>
                  <FilterLabel>📅 Filter by date</FilterLabel>
                  <FilterField>
                    <FilterLabel style={{ color: '#B0A0D0', textTransform: 'none', letterSpacing: 0, fontSize: TYPE.caption, fontWeight: 600 }}>From</FilterLabel>
                    <DateInput type="date" value={scheduleFromDate} max={scheduleToDate || undefined} onChange={e => setScheduleFromDate(e.target.value)} />
                  </FilterField>
                  <FilterField>
                    <FilterLabel style={{ color: '#B0A0D0', textTransform: 'none', letterSpacing: 0, fontSize: TYPE.caption, fontWeight: 600 }}>To</FilterLabel>
                    <DateInput type="date" value={scheduleToDate} min={scheduleFromDate || undefined} onChange={e => setScheduleToDate(e.target.value)} />
                  </FilterField>
                  {isDateFiltered && (
                    <>
                      <FilterResultPill>{allFilteredScheduledInterviews.length} interview{allFilteredScheduledInterviews.length !== 1 ? 's' : ''}</FilterResultPill>
                      <ClearFilterBtn onClick={() => { setScheduleFromDate(''); setScheduleToDate(''); }}>✕ Clear</ClearFilterBtn>
                    </>
                  )}
                </CardToolbar>
                <TableScroll>
                <StyledTable>
                  <TableHead><TableRow>
                    <HeadCell>JD ID</HeadCell><HeadCell>Job Role</HeadCell><HeadCell>Candidate</HeadCell>
                    <HeadCell>Interviewer</HeadCell><HeadCell>Scheduled</HeadCell>
                    <HeadCell sx={{ textAlign: 'right' }}>Actions</HeadCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {scheduledLoading && data.length === 0 ? (
                      <BodyRow>
                        <BodyCell colSpan={6} sx={{ textAlign: 'center' }}>
                          <CircularProgress size={28} sx={{ color: '#6750A4', my: 2 }} />
                        </BodyCell>
                      </BodyRow>
                    ) : filteredScheduledInterviews.length === 0 ? (
                      <BodyRow>
                        <BodyCell colSpan={6} sx={{ textAlign: 'center', color: '#B0A0D0 !important', py: '40px !important' }}>
                          {scheduledError ? 'Interviews could not be loaded.' : isDateFiltered ? 'No interviews found in this date range' : 'No interviews scheduled'}
                        </BodyCell>
                      </BodyRow>
                    ) : (
                      filteredScheduledInterviews.map(row => {
                        const interviewers = Array.isArray(row.interviewer) && row.interviewer.length > 0 ? row.interviewer.join(', ') : '';
                        const candidateEmail = row.candidate?.candidateEmail;
                        return (
                          <BodyRow key={row._id}>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>{row.jddetails_id ? <LinkButton onClick={() => handleViewJD(row.jddetails_id)}>{row.jddetails_id.jdID || '—'}</LinkButton> : '—'}</BodyCell>
                            <BodyCell sx={{ fontWeight: '600 !important' }}><CellClamp minWidth={220} title={row.jddetails_id?.jobRole || undefined}>{row.jddetails_id?.jobRole}</CellClamp></BodyCell>
                            <BodyCell>
                              <Box sx={{ whiteSpace: 'nowrap' }}><LinkButton onClick={() => handleViewCandidateData(row.candidate)}>{row.candidate?.candidateID}</LinkButton></Box>
                              <CellEllipsis maxWidth={220} title={candidateEmail || undefined} style={{ fontSize: TYPE.caption, color: '#8B7DB5', lineHeight: 1.5 }}>{candidateEmail}</CellEllipsis>
                            </BodyCell>
                            <BodyCell sx={{ fontSize: `${TYPE.caption}px !important`, color: '#8B7DB5 !important' }}>
                              <CellEllipsis maxWidth={180} title={interviewers || undefined}>{interviewers || '—'}</CellEllipsis>
                            </BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatDisplayDateTime(row.dateAndTime)}</BodyCell>
                            <BodyCell sx={{ textAlign: 'right' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                <a href={row.meet_link} target="_blank" rel="noopener noreferrer" style={{ color: '#6750A4', fontWeight: 600, fontSize: TYPE.small, fontFamily: FONT_BODY, textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' }}>Join</a>
                                <IconButton size="small" onClick={() => handleOpenReschedule(row)}
                                  sx={{ width: 32, height: 32, color: '#6750A4', '&:hover': { background: 'rgba(103,80,164,0.08)' } }}>
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </BodyCell>
                          </BodyRow>
                        );
                      })
                    )}
                  </TableBody>
                </StyledTable>
                </TableScroll>
                </TableCard>
                {filteredScheduledInterviews.length > 0 && totalPages.scheduledInterviews > 1 && (
                  <Pagination itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage.scheduledInterviews} totalPages={totalPages.scheduledInterviews} handlePrevPage={() => handlePrevPage('scheduledInterviews')} handleNextPage={() => handleNextPage('scheduledInterviews')} />
                )}
              </StyledPaper>
            )}

            {/* ── Upload ── */}
            {activeTab === "upload" && (
              <StyledPaper>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <IconButton onClick={() => setActiveTab(uploadSource === "dashboard" ? "RecruiterDashboard" : "resumes")} sx={{ width: 36, height: 36, background: '#F3EEF9', borderRadius: `${RADIUS.sm}px`, '&:hover': { background: '#EDE7F6' } }}>
                    <ArrowBackIcon sx={{ color: '#4F378B' }} />
                  </IconButton>
                  <Box>
                    <PageTitle>Upload Resumes</PageTitle>
                    <PageSubtitle sx={{ mb: '0 !important' }}>Add candidates to the talent pool</PageSubtitle>
                  </Box>
                </Box>
                <SectionCard>
                {!selectedJD && (
                  <FormControl size="small" sx={{ mb: 3, width: '100%', maxWidth: 360, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT.lg } }} required>
                    <InputLabel sx={{ fontFamily: FONT_BODY }}>Select Domain</InputLabel>
                    <Select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} sx={{ borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY }}>
                      <MenuItem value="aiml">AIML</MenuItem>
                      <MenuItem value="embedded">Embedded</MenuItem>
                      <MenuItem value="Devops">DevOps</MenuItem>
                      <MenuItem value="Java">Java</MenuItem>
                      <MenuItem value="SAP">SAP</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <UploadZone onClick={() => { if (!uploading) fileInputRef.current?.click(); }}>
                  <CloudUploadIcon sx={{ fontSize: 64, color: '#7C5CBF' }} />
                  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, lineHeight: 1.25, color: '#4F378B' }}>Click to upload resumes</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: '#8B7DB5' }}>Supports PDF, DOC, DOCX</Typography>
                </UploadZone>
                <input type="file" id="fileInput" ref={fileInputRef} accept=".pdf,.doc,.docx" multiple hidden onChange={handleFileChange} />
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2, width: '100%', maxWidth: 560 }}>
                    <List dense>
                      {selectedFiles.map((file, index) => (
                        <ListItem key={index} sx={{ background: '#F8F4FF', borderRadius: `${RADIUS.sm}px`, mb: 1 }}
                          secondaryAction={<IconButton edge="end" disabled={uploading} onClick={(e) => handleRemoveFile(index, e)}><DeleteIcon sx={{ color: '#C2185B', fontSize: 18 }} /></IconButton>}>
                          <ListItemText primary={file.name} primaryTypographyProps={{ style: { fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#3D2B6B' } }} />
                        </ListItem>
                      ))}
                    </List>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2 }}>
                      <GhostButton disabled={uploading} onClick={(e) => { e.preventDefault(); setSelectedFiles([]); resetFileInput(); }}>Clear All</GhostButton>
                      <PrimaryButton
                        disabled={uploading}
                        onClick={(e) => handleResumeSubmit(e, selectedJD)}
                        startIcon={uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                      >
                        {uploading ? "Submitting…" : "Submit"}
                      </PrimaryButton>
                    </Box>
                  </Box>
                )}
                </SectionCard>
              </StyledPaper>
            )}

            {/* ── Sub-pages ── */}
            {showCandidateProfiles && selectedCandidateProfileId && activeTab !== 'Scheduled' && (
              <CandidateProfiles jdId={selectedCandidateProfileId} userType={selectedUserType} recruiterEmail={userData?.email} skills={selectedJDSkills} experience={selectedJDExperience} jobDescription={jobDescription} salaryRange={salaryRange} companyName={companyName} readOnly={readOnly} onBack={handleClose} />
            )}

            {isViewJdDetailsOpen && (
              <JDDetails jdDetails={selectedJD} userRole="recruiter" recruiterEmail={userData?.email} jobDescription={jobDescription} salaryRange={salaryRange} companyName={companyName} readOnly={selectedJD?.readOnly} onBack={handleClose} />
            )}

            {activeTab === 'pendingData' && (
              <StyledPaper><PendingData key={tabRefreshKey} email={userData?.email} userType={selectedUserType || userType} onBack={() => switchTab('RecruiterDashboard')} /></StyledPaper>
            )}

            {activeTab === 'resumeDisplay' && resumeData && <ResumeDisplay resumeData={resumeData} />}

            {showCandidateData && selectedCandidate && (
              <CandidateData
                candidateId={selectedCandidate._id}
                userRole={userType}
                candidateEmail={selectedCandidate.candidateEmail || "N/A"}
                overallStatus={selectedCandidate.overallStatus || "N/A"}
                jdId={selectedCandidate.jddetails_id?.jdID || undefined}
                jobDescription={selectedCandidate.jddetails_id?.jobDescription || "N/A"}
                salaryRange={selectedCandidate.jddetails_id?.salaryRange || "N/A"}
                companyName={selectedCandidate.jddetails_id?.companyName || undefined}
                recruiterEmail={recruiterEmail}
                skills={selectedCandidate.skills || "N/A"}
                experience={selectedCandidate.experience || "N/A"}
                interviewid={selectedCandidate.interviewid || "N/A"}
                onBack={handleClose}
              />
            )}
            <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)}
              PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px`, minWidth: 420, padding: '8px' } }}>
              <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E' }}>
                Reschedule Interview
              </DialogTitle>
              <DialogContent>
                {rescheduleRow && (
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, lineHeight: 1.5, color: '#8B7DB5', mb: 2 }}>
                    {rescheduleRow.candidate?.candidatename} — {rescheduleRow.jddetails_id?.jobRole}
                  </Typography>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="New Date" type="date" InputLabelProps={{ shrink: true }}
                    value={rescheduleDate} onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleError(''); }}
                    inputProps={{ min: toLocalDateInput(new Date()) }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } }}
                  />
                  <TextField
                    label="New Time" type="time" InputLabelProps={{ shrink: true }}
                    value={rescheduleTime} onChange={(e) => { setRescheduleTime(e.target.value); setRescheduleError(''); }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } }}
                  />
                </Box>
                <Autocomplete
                  multiple freeSolo
                  options={[]}
                  value={rescheduleInterviewers}
                  onChange={(e, value) => setRescheduleInterviewers(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Interviewer Email(s)"
                      placeholder="Add or remove interviewers"
                      sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } }} />
                  )}
                />
                <TextField
                  label="Reason for reschedule (optional)"
                  fullWidth multiline rows={2}
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } }}
                />
                {rescheduleError && (
                  <Typography sx={{ color: '#E53935', fontFamily: FONT_BODY, fontSize: TYPE.small, lineHeight: 1.5, mt: 1.5 }}>
                    {rescheduleError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ padding: '16px 24px', gap: 1.5 }}>
                <GhostButton onClick={() => setRescheduleOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={handleRescheduleSubmit} disabled={rescheduling}>
                  {rescheduling ? 'Saving…' : 'Save Changes'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            <NotificationDialog
              open={snackbarOpen}
              message={snackbarMessage}
              onClose={() => setSnackbarOpen(false)}
            />
            <QuestionDialog open={open} setOpen={setOpen} jdId={questionsJdId} mcqQuestions={mcqQuestions} codingQuestions={codingQuestions} subjectiveQuestions={subjectiveQuestions} onQuestionsUpdated={() => fetchQuestions(questionsJdId)} performedBy={email} role={userType} />

          </ScrollableContent>
        </CenteredSection>
      </Container>
    </ThemeProvider>
  );
}

export default RecruiterDashboard;